import { useResumableString } from '../../lib/session-resume';
import { useState } from 'react';
import type { ActiveSession } from '../../lib/auth';
import { pinRequest, validStaffPin } from '../../lib/staff-pin';
import { roleLabel } from './display-copy';
const roles = [
  'coordinator',
  'rd',
  'rd_checker',
  'qa',
  'owner',
  'finance',
  'purchasing',
  'designer',
  'admin',
  'auditor',
  'production',
];
export function StaffAccountForm({
  session,
  organizationId,
}: {
  session: ActiveSession;
  organizationId: string;
}) {
  const recoveryForm = 'staff-account:' + session.userId + ':' + organizationId;
  const [name, setName] = useResumableString(recoveryForm, 'name'),
    [pin, setPin] = useState(''),
    [role, setRole] = useResumableString(recoveryForm, 'role', 'production'),
    [projectId, setProjectId] = useResumableString(recoveryForm, 'projectId'),
    [expires, setExpires] = useResumableString(recoveryForm, 'expiresAt', () =>
      new Date(Date.now() + 86400000 - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16),
    ),
    [key, setKey] = useResumableString(recoveryForm, 'requestKey'),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState('');
  return (
    <details>
      <summary>เพิ่มบัญชีทีมงาน</summary>
      <p>
        ชื่อจะแสดงในหน้าเข้าสู่ระบบ ให้เจ้าของบัญชีกรอกรหัสส่วนตัวของตนเอง หากยังไม่เลือกโครงการ
        บัญชีจะไม่มีสิทธิ์งานใด
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const idempotencyKey = key || crypto.randomUUID();
          setKey(idempotencyKey);
          setBusy(true);
          void pinRequest(
            '/staff-accounts',
            {
              organizationId,
              idempotencyKey,
              name,
              pin,
              role,
              projectId: projectId || null,
              evidence: 'เพิ่มบัญชีทีมงานพร้อมรหัสส่วนตัว',
              expiresAt: new Date(expires).toISOString(),
            },
            true,
          )
            .then(
              () => {
                setPin('');
                setKey('');
                setNotice('สร้างบัญชีแล้ว เลือกชื่อในหน้าเข้าสู่ระบบทีมงาน');
              },
              () => setNotice('สร้างบัญชีไม่ได้ ลองคำขอเดิมอีกครั้งหรือตรวจสอบสิทธิ์และชื่อที่ซ้ำ'),
            )
            .finally(() => setBusy(false));
        }}
      >
        <label>
          ชื่อที่แสดง
          <input
            required
            maxLength={100}
            value={name}
            disabled={busy || Boolean(key)}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label>
          รหัสส่วนตัว 6 หลัก
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]{6}"
            minLength={6}
            maxLength={6}
            autoComplete="new-password"
            required
            value={pin}
            disabled={busy}
            onChange={(e) => setPin(e.target.value)}
          />
        </label>
        <label>
          โครงการ (เลือกภายหลังได้)
          <select
            value={projectId}
            disabled={busy || Boolean(key)}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">ยังไม่ให้สิทธิ์โครงการ</option>
            {session.contexts
              .filter((c) => c.organizationId === organizationId && c.audience === 'staff')
              .map((c) => (
                <option key={c.projectId} value={c.projectId}>
                  {c.label}
                </option>
              ))}
          </select>
        </label>
        <label>
          หน้าที่เมื่อให้สิทธิ์โครงการ
          <select
            value={role}
            disabled={busy || Boolean(key)}
            onChange={(e) => setRole(e.target.value)}
          >
            {roles.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        </label>
        <label>
          บัญชีเข้าสู่ระบบได้ถึง (เวลาท้องถิ่น)
          <input
            type="datetime-local"
            required
            value={expires}
            disabled={busy || Boolean(key)}
            onChange={(e) => setExpires(e.target.value)}
          />
        </label>
        <button className="action" disabled={busy || !navigator.onLine || !validStaffPin(pin)}>
          {key ? 'ลองคำขอเดิมอีกครั้ง' : 'เพิ่มบัญชีทีมงาน'}
        </button>
      </form>
      {key ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setKey('');
            setPin('');
            setNotice('');
          }}
        >
          เริ่มคำขอใหม่
        </button>
      ) : null}
      {notice ? <p role="status">{notice}</p> : null}
    </details>
  );
}
